import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";
import { TONE_CLASSES, faceOf } from "../subscriptionState";
import type { Subscription } from "../types/subscriptions.types";
import PlanIncludes from "./PlanIncludes";

/** An ISO date as the card writes it, or a dash where there is none yet. */
const day = (iso: string | null): string =>
  iso ? formatCreatedOn(new Date(iso)) : "-";

interface SubscriptionStatusCardProps {
  subscription: Subscription;
  /** What the product is called — "Naurist Pro". */
  productName: string;
  /** The term it is billed on, in words — "Monthly", "Weekly". Off the
   *  subscription's own record where it has one; see `BillingSection`. */
  cycleLabel: string;
  /** Sends somebody back through the checkout to sign a new mandate. */
  onFixMandate: () => void;
}

/**
 * The plan the workspace is on, at the top of Billing.
 *
 * It leads with what was bought rather than with what state it is in, because
 * for all but a few days of a subscription's life the state is "fine" and the
 * question being asked is "what am I paying for". What is included is folded
 * away under a disclosure for the same reason: it is the answer to a question
 * asked once, not something to scroll past every visit.
 *
 * The two states nobody plans for are still what the card is built around. A
 * renewal that has failed says so loudly and offers the one action that fixes
 * it — `HALTED` is where subscription revenue leaks, the product is locked, and
 * nothing changes until a new mandate is signed. A cancelled plan says when
 * access stops rather than when it renews, which is a different date with the
 * same shape.
 *
 * ## Why there is no Cancel plan on it
 *
 * There was, beside "What's included", and it was the second thing read about
 * something just paid for. Cancelling lives at the foot of the section now —
 * see `CancelPlanCard` — where it is the last thing on the screen rather than
 * the first thing offered. This card is what you are paying for and when; what
 * to do about no longer wanting it is a different question, asked further down.
 */
const SubscriptionStatusCard: React.FC<SubscriptionStatusCardProps> = ({
  subscription,
  productName,
  cycleLabel,
  onFixMandate,
}) => {
  const face = faceOf(subscription.status);
  const needsAction = face.tone === "attention";

  /**
   * Whether there is a charge still to come, which is what decides how the
   * date beside the plan is headed.
   *
   * A plan that has been called off, or one that is over, still has a cycle
   * running to its end — the same date, and it must not be called the next
   * billing date, because nothing is being billed. It is the day access stops.
   */
  const renews =
    !subscription.cancelledOn &&
    !["CANCELLED", "COMPLETED", "EXPIRED"].includes(subscription.status);

  /**
   * A plan called off that is still running out the cycle it is paid up to.
   *
   * The one thing the status pill cannot say. Cancelling leaves the record
   * `ACTIVE` — which is true, and is why the product stays unlocked — until the
   * day the cycle ends, so the badge reads "Active" and goes on reading it.
   * Without something beside it the whole of the news is a date heading
   * changing from "Next billing date" to "Access until", which is not enough to
   * tell somebody their cancellation went through.
   *
   * It is `cancelledOn` and not the status that answers this; see
   * `cancelSubscription`, where `cancel_requested_at` is the only field the
   * endpoint changes.
   */
  const autoRenewalOff = !!subscription.cancelledOn && face.tone === "good";

  return (
    <section
      aria-labelledby="current-plan-heading"
      className={`card-surface border bg-white ${
        needsAction ? "border-[#E7E8EC]" : "border-[#E7E8EC]"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4 px-5 pt-4 pb-3 3xl:px-6 3xl:py-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2
              id="current-plan-heading"
              className="text-[20px] 3xl:text-[24px] font-semibold text-ink"
            >
              {productName}
            </h2>
            {/* The cycle is outlined and the state is filled: one is what is
                being charged, the other is a fact about today, and they must
                not read as two of the same thing.

                Only while something is still due. "Monthly" on a plan that has
                been called off names a rhythm that has stopped — there is one
                cycle left and then nothing, and the date on the right already
                says when that is. */}
            {renews && (
              <span className="inline-flex whitespace-nowrap rounded-full border border-line px-2 py-0.5 text-[12px] 3xl:text-[13px] text-[#00424B]">
                {cycleLabel}
              </span>
            )}
            <span
              className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[12px] 3xl:text-[13px] font-medium ${
                TONE_CLASSES[face.tone]
              }`}
            >
              {face.label}
            </span>
            {/* Beside "Active" rather than instead of it, because both are
                true: the plan is running and it will not renew. Orange, and
                deliberately neither of the two colours already on this card:
                loud enough to be read, not the red that means a payment has
                failed. See `autoRenewalOff`. */}
            {autoRenewalOff && (
              <span className="inline-flex whitespace-nowrap rounded-full border-[0.5px] border-[#F78317] bg-[#FFF4E9] px-2 py-0.5 text-[12px] 3xl:text-[13px] font-medium text-[#F78317]">
                Auto-renewal off
              </span>
            )}
          </div>

          <p className="mt-1.5 text-[13px] 3xl:text-[14px] text-ink-muted">
            One plan. Complete access
          </p>

          {/* Only when there is something to explain. A running plan needs no
              sentence about itself — the pill above already said it — and one
              printed anyway is a line the eye learns to skip, which is exactly
              the line that has to be read on the day it changes.

              A plan called off says it in the pill beside the status and not
              here, for the same reason: two badges and a date heading carry it
              without adding a sentence to a card whose whole job is to be
              glanced at. */}
          {face.tone !== "good" && (
            <p className="mt-1.5 text-[13px] 3xl:text-[14px] leading-[1.5] text-ink-muted">
              {face.detail}
            </p>
          )}
        </div>

        {/* When the next charge lands, rather than the day this was bought.
            The purchase is in the history below with its invoice against it;
            what somebody opens Billing to check is what is about to leave
            their account and when. */}
        {/* Beside the plan where there is room for it, and its own line on a
            phone: wrapped under the name, a right-aligned block reads as
            something that fell off the row above rather than as the date this
            card is half about. `w-full` is what makes it a line rather than a
            column pushed to the end of one. */}
        <div className="w-full text-left md:w-auto md:text-right">
          <p className="text-[12px] 3xl:text-[13px] text-ink-muted">
            {renews ? "Next billing date" : "Access until"}
          </p>
          <p className="mt-0.5 text-[16px] 3xl:text-[18px] font-semibold text-ink">
            {day(subscription.currentEnd)}
          </p>
        </div>
      </div>

 

      {/* The same list a trial's card folds away, and the same disclosure —
          there is one product, and what it includes must not drift between the
          two ways of having it. See `PlanIncludes`. */}
      <PlanIncludes />

      {needsAction && (
        // Under everything else rather than above it: what to do about it only
        // makes sense once you have seen what is being charged and when.
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#F3B9B9] bg-[#FEF6F6] px-5 py-4 3xl:px-6">
          <p className="flex items-start gap-2 text-[13px] 3xl:text-[14px] text-[#8C2F2F]">
            <AlertTriangle
              aria-hidden="true"
              className="mt-0.5 h-4 w-4 flex-shrink-0"
            />
            Set up the mandate again to keep your workspace running.
          </p>
          {/* One action, and it is the one that mends this. Stopping the plan
              instead is at the foot of the section like it is on any other
              day — a row that offers both puts the way out beside the way
              back, on the screen of somebody whose payment has just failed.

              Full width on a phone, where it is the last thing on a card and
              the thing the card exists to get pressed — a button sized to its
              own words there is a small target under a sentence about a
              failed payment. */}
          <Button
            variant="primary"
            size="sm"
            onClick={onFixMandate}
            className="w-full md:w-auto"
          >
            Fix payment method
          </Button>
        </div>
      )}
    </section>
  );
};

export default SubscriptionStatusCard;
